import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {StatoWeekPLUSRecord} from "@commons/mapping/StatoWeekPLUSRecord";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {HHWeekItems, LavoratoreProprietaOrarioRecord} from "@commons/mapping/LavoratoreProprietaOrarioRecord";
import {DateTime} from "luxon";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {IndiceMese, Periodo} from "@commons/types";
import {CachePresenze} from "@commons/mapping/CachePresenze";
import {LavoratoreStatusRecord} from "@commons/mapping/LavoratoreStatusRecord";
import {LavoratorePresenzeProprietaULRecord} from "@commons/mapping/LavoratorePresenzeProprietaULRecord";
import {CalendarioChiusureRecord} from "@commons/mapping/CalendarioChiusureRecord";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import {FlexRecord} from "@commons/mapping/FlexRecord";
import {QualificaEccRecord} from "@commons/mapping/QualificaEccRecord";
import {QuantificaEccRecord} from "@commons/mapping/QuantificaEccRecord";
import {TurnoDistribuzioneRecord} from "@commons/mapping/TurnoDistribuzioneRecord";
import {GiustificazioneRecord} from "@commons/mapping/GiustificazioneAutoRecord";
import {cloneDeep} from "lodash";
import {TabellaAlternativiTurniRecord} from "@commons/mapping/TabellaAlternativiTurniRecord";
import {GiustificativoAttestatoINPSRecord} from "@commons/mapping/GiustificativoAttestatoINPSRecord";
import {LavoratoreCalcolo} from "@commons/services/presenze_calcolo/Calcolo";

export type HHWeekStatus = {
    totOrdPrevisteTurno: number, // quantità oraria prevista dal turno (espressa in MINUTI)
    totOrdPreviste: number,
    hhMin: number, // quantità oraria minima settimanale espressa in MINUTI, es 20h=>1200
    hhMax: number, // quantità oraria massima settimanale espressa in MINUTI, es 40h=>2400
    massimale: number, // massimale sempre 48h=>2880
    hhInizio: number, // quantità oraria inizio settimana espressa in MINUTI, es 20h=>1200
    hhFine: number, // quantità oraria fine settimana espressa in MINUTI, es 40h=>2400
    sviluppoBasso: boolean, // helper per segnalare l'anomalia
    sviluppoAlto: boolean, // helper per segnalare l'anomalia
    sviluppoNonConforme: boolean,
    forzabile: boolean,
};

export interface ConsolidatedData {

    proprietaOrario: LavoratoreProprietaOrarioRecord | null,

    // Aggiorno elenco stati e proprietà di gestione
    elencoStatus: LavoratoreStatusRecord[],
    proprietaGestione: LavoratorePresenzeProprietaULRecord | null,

    calendarioChiusure: CalendarioChiusureRecord | null,

    elencoTurni: TurnoRecord[],
    turnoAConsuntivo: TurnoRecord | null,

    tabFlex: FlexRecord | null,
    tabQualificaEcc: QualificaEccRecord | null,
    tabQuantificaEcc: QuantificaEccRecord | null,
    distribuzioneTurno: TurnoDistribuzioneRecord | null,

    //calcolabile: boolean,
}

export interface EnvironmentData {
    anno: number,
    mese: IndiceMese,
    soloMeseRif: boolean,

    days: PresenzeGiornataPlusRecord[] | null,

    weeks: StatoWeekPLUSRecord[] | null,
    totaliMese: StatoWeekPLUSRecord | null,

    causaliWF: PresenzeCausaleWFRecord[],
    causaliWFElaborate: string[],
    giustificativiAttestati: GiustificativoAttestatoINPSRecord[],
    giustificativiAttestatiElaborati: string[],
}

export interface GestionePlusData extends EnvironmentData, ConsolidatedData {
}

export class GestionePlusData {

    /**
     * Valorizza e restituisce un oggetto di tipo HHWeekStatus in base alla settimana indicata in week
     * @param data
     * @param lavoratore
     * @param week
     */
    static getMinMaxHContr = (data: GestionePlusData, lavoratore: LavoratoreCalcolo, week: StatoWeekPLUSRecord): HHWeekStatus => {
        //return ctrl.week.totali.getTotaleHContr(); fino al 8/1/2020

        // Fisso campo per la determinazione delle ore settimanali medie
        //const item: HHWeekItems = 'hhWeekMedie'; fino al 9/10/2023
        const item: HHWeekItems = 'hhWeekMedieDistribuzione';

        const dtInizioWeek = DateTime.fromISO(week.dtInizioWeek!);
        const dtFineWeek = DateTime.fromISO(week.dtFineWeek!);
        const forzabileXAssCess = LavoratorePresenzeRecord.isHiredBetween(lavoratore, week.dtInizioWeek, week.dtFineWeek, false) ||
            LavoratorePresenzeRecord.isFiredBetween(lavoratore, week.dtInizioWeek, week.dtFineWeek, false);

        const orarioProprietaInizio = LavoratorePresenzeRecord.getOrarioProprieta(lavoratore!, dtInizioWeek);
        const orarioProprietaFine = LavoratorePresenzeRecord.getOrarioProprieta(lavoratore!, dtFineWeek);
        const hhFine = orarioProprietaFine !== null ? orarioProprietaFine[item] * 60 : 0;
        //var hhInizio = orarioProprietaInizio !== null ? orarioProprietaInizio.hhWeekMedie * 60 : 0; fix del 28/7/2021, se null perché alla data inizio week non era in forza allora fisso hhWeek fine week!
        const hhInizio = orarioProprietaInizio !== null ? orarioProprietaInizio[item] * 60 : hhFine;
        const forzabileXVariazioneHH = hhFine !== hhInizio;

        let totOrdPrevisteTurno = 0;
        let totOrdPreviste = 0;
        if (data.days) {
            for (let day = 0; day < 7; day++) {		// per sette giorni...
                const giornata = data.days[week.index * 7 + day];
                if (giornata && giornata.totali !== null) {
                    totOrdPrevisteTurno += giornata.totali.hPrevDiurneTurno;
                    totOrdPreviste += giornata.totali.hPrevDiurne;
                }
            }
        }
        const result = {
            totOrdPrevisteTurno: totOrdPrevisteTurno,
            totOrdPreviste: totOrdPreviste,
            hhMin: hhInizio < hhFine ? hhInizio : hhFine,
            hhMax: hhInizio < hhFine ? hhFine : hhInizio,
            massimale: 2880,
            hhInizio: hhInizio,
            hhFine: hhFine,
            sviluppoBasso: false,
            sviluppoAlto: false,
            sviluppoNonConforme: false,
            forzabile: forzabileXAssCess || forzabileXVariazioneHH,
        };
        if (result.hhMin === 0 && result.hhMax === 0) {
            // SOLO per lavoratori "a chiamata" (20/02/2023)
            result.hhMax = 2400;
            result.sviluppoBasso = false; // Sempre false, può fare anche zero ore!
            result.sviluppoAlto = (result.totOrdPreviste > result.hhMax);
        } else {
            if (forzabileXVariazioneHH) {
                result.sviluppoBasso = (result.totOrdPreviste < result.hhMin);
                result.sviluppoAlto = (result.totOrdPreviste > result.hhMax);
            } else if (forzabileXAssCess) { // hhMin===hhMax
                result.sviluppoBasso = (result.totOrdPreviste < result.hhMin);
                result.sviluppoAlto = (result.totOrdPreviste > result.hhMin);
            } else { // hhMin===hhMax e in forza x tutta la week
                result.sviluppoBasso = (result.totOrdPreviste < result.totOrdPrevisteTurno);
                result.sviluppoAlto = (result.totOrdPreviste > result.totOrdPrevisteTurno);
            }
        }
        result.sviluppoNonConforme = result.sviluppoBasso || result.sviluppoAlto;
        return result;
    };

    /**
     * Aggiorna la settimana (totali ed eventuali anomalie!)
     * @param data
     * @param lavoratore
     * @param week la settimana da aggiornare, istanza di StatoWeekPLUSRecord
     * @param cache
     * @param mese
     */
    static updateTotaliWeek = (data: GestionePlusData, lavoratore: LavoratoreCalcolo, week: StatoWeekPLUSRecord, cache: CachePresenze, mese: IndiceMese = data.mese) => {
        const iWeek = week.index;
        if (iWeek >= 0) {
            // Fisso inizialmente a false (settimana nn presenze)
            week.hired = false;

            RiepilogoOrePlus.clear(week.totali);
            week.giustificativi = [];

            StatoWeekPLUSRecord.totalizzaWeek(data.days!, cache, week, iWeek, false, mese);
            //const hhWeeks = this.getMinMaxHContr(week);
            const hhWeeks = GestionePlusData.getMinMaxHContr(data, lavoratore, week);

            /*
                        const previsto = RiepilogoOrePlus.getTotaleHPrev(week.totali);
                        if (hhWeeks.hhMin === 0 && hhWeeks.hhMax === 0) {
                            // SOLO per lavoratori a chiamata (20/02/2023)
                            hhWeeks.hhMax = 2400;
                            week.anomalia = previsto > hhWeeks.hhMax;
                        } else {
                            week.anomalia = !(previsto <= hhWeeks.totOrdPrevisteTurno && previsto >= hhWeeks.hhMin);
                        }
            */
            week.anomalia = hhWeeks.sviluppoBasso || hhWeeks.sviluppoAlto;
            week.forzabile = false;

            /*week.warning = false;
            if (week.totali.getTotaleHLav() > 48) {
              week.warning = true;
            }*/

            // aggiorno warning e alert superiori

            // aggiorno alert inferiori

            if (week.anomalia) {

                if ((week.basiHHPeriodo > 1) ||
                    (week.giorniPeriodo < 7) ||
                    (week.ULPeriodo > 1) ||
                    //LavoratorePresenzeRecord.isHiredBetween(lavoratore!, week.dtInizioWeek, week.dtFineWeek) ||
                    //LavoratorePresenzeRecord.isFiredBetween(lavoratore!, week.dtInizioWeek, week.dtFineWeek)
                    hhWeeks.forzabile
                ) {
                    //if (totOrdPreviste<=40)	// Migliorabile, si potrebbe valutare un massimale profilato sull'azienda
                    // solo se le ore sono minori o uguali al massimale, verifico la sussistenza delle condizioni di chiusura forzata
                    week.forzabile = true;
                }
            }

            //this.aggiornaSaldiAccumuli(week);
            return week;

        } else {
            console.error("Indice settimana non inizializzato!");
            return null;
        }
    }

    static consolidaGiornata = (periodo: Pick<Periodo, 'anno'>,
                                lavoratore: LavoratoreCalcolo,
                                day: PresenzeGiornataPlusRecord,
                                cache: CachePresenze,
                                isPaginaWF: boolean,
                                ignoraStatus: boolean = true) => {
        const getTurno = (codTurno: number) => cache.getTurno(codTurno);
        const that: ConsolidatedData = {
            calendarioChiusure: null,
            distribuzioneTurno: null,
            elencoStatus: [],
            elencoTurni: [],
            proprietaGestione: null,
            proprietaOrario: null,
            tabFlex: null,
            tabQualificaEcc: null,
            tabQuantificaEcc: null,
            turnoAConsuntivo: null
        }
        const dtGiornata = DateTime.fromISO(day.dtGiornata!);

        let calcolabile = true; // La giornata è calcolabile
        let statusSuAstensione = false; // Se true, la giornata ha uno status di giustificazione di astensione

        console.log('Nominativo:', lavoratore!.cognome, lavoratore!.nome);
        // Aggiorno proprietà orario per cambio distribuzione
        that.proprietaOrario = LavoratorePresenzeRecord.getOrarioProprieta(lavoratore!, dtGiornata);

        // Aggiorno elenco stati e proprietà di gestione
        that.elencoStatus = LavoratorePresenzeRecord.getStatus(lavoratore!, day.dtGiornata!);
        that.proprietaGestione = LavoratorePresenzeRecord.getProprieta(lavoratore!, dtGiornata);

        // Fisso calendario chiusure!!!
        const idCalendario = that.proprietaGestione ? (isPaginaWF ? that.proprietaGestione.idCalendarioWF : that.proprietaGestione.idCalendario) : 0;
        that.calendarioChiusure = cache.getCalendario(idCalendario, periodo.anno);

        if ((day.tipoRilevazione === 'MANUALE')) {
            // Clono proprietà al fine di preservare le impostazioni originali per la modalità plus
            that.proprietaGestione = LavoratorePresenzeProprietaULRecord.factory(that.proprietaGestione ? that.proprietaGestione : undefined);
            that.proprietaGestione.quantitaTimbrature = 'CON_O_SENZA'; // Forzo modalità con o senza!

            // Se ignoraStatus è true allora non valuto la presenza di astensioni da status.
            // TODO: Capire bene implicazioni
            // 21/3/2025, quando serve valutare se la giornata è calcolabile, ignoraStatus è sempre true, direi che si tratta di un check utilizzato in passato
            // per valutare chi sa quale situazione...
            if (!ignoraStatus) {
                for (let indStatus = 0; indStatus < that.elencoStatus.length; ++indStatus) {
                    const status = that.elencoStatus[indStatus];
                    if (status.astensione && GiustificazioneRecord.isValid(status.astensione)) {
                        statusSuAstensione = true;
                        break;
                    }
                }
                if (!statusSuAstensione) {
                    calcolabile = false;
                }
            }
        }

        // Aggiorno elenco turni
        that.elencoTurni = [];
        that.turnoAConsuntivo = null;

        if (that.proprietaGestione) {
            // Prelevo turno base (turno determinato allo sviluppo)
            const turno = getTurno(day.codTurnoPianificazione > 0 ? day.codTurnoPianificazione : day.codTurnoSviluppo);
            if (turno) {
                const turnoBase = cloneDeep(turno);
                turnoBase.tipologia = '_BASE';

                // Istanzio turno fittizio per gestione a consuntivo, va rifatto ogni volta al fine di prelevare tutte le properties del turno di base!
                that.turnoAConsuntivo = cloneDeep(turno);
                that.turnoAConsuntivo.codTurno = -1;
                that.turnoAConsuntivo.descrizione = 'Orario a consuntivo';
                that.turnoAConsuntivo.tipologia = '_CONSUNTIVO';
                // Riassegno properties oggetto perché quelle della nuova istanza non sono castate correttamente... (sono oggetti generici e non tipizzati!)
                // NON PIU' VALIDO IN REACT
                //that.turnoAConsuntivo.turnazioni = turno.turnazioni;
                //that.turnoAConsuntivo.distribuzione = turno.distribuzione;
                //that.turnoAConsuntivo.tabelle = turno.tabelle;

                // Imposto turno corrente
                /*if (day.currentTurno === null) {
                    day.currentTurno = getTurno(day.codTurno); // Consolido istanza turno
                }*/
                that.elencoTurni.push(turnoBase);
            }

            if (that.proprietaGestione.quantitaTimbrature === 'A_CONSUNTIVO') {
                if (that.turnoAConsuntivo)
                    that.elencoTurni.push(that.turnoAConsuntivo);
            } else {
                const tabelle = LavoratorePresenzeProprietaULRecord.getTabelle(that.proprietaGestione, turno!); // TabellePresenze.js
                if (tabelle && tabelle.idTabTurniAlt > 0) {
                    const tabellaAlt = cache.getTurniAlternativi(tabelle.idTabTurniAlt);
                    if (tabellaAlt && tabellaAlt.turniAlternativi) {
                        for (let ind0 = 0; ind0 < tabellaAlt.turniAlternativi.length; ++ind0) {
                            if (day.codTurnoSviluppo !== tabellaAlt.turniAlternativi[ind0].distribuzione.codTurno &&
                                day.codTurnoPianificazione !== tabellaAlt.turniAlternativi[ind0].distribuzione.codTurno) { // La pianificazione può fissare un alternativo come turno base!
                                const turno = getTurno(tabellaAlt.turniAlternativi[ind0].distribuzione.codTurno);
                                if (turno) {
                                    const turnoAlt = cloneDeep(turno);
                                    turnoAlt.tipologia = TabellaAlternativiTurniRecord.isAutomatico(tabellaAlt.turniAlternativi[ind0]) ? 'ALT_AUTO' : 'ALT_MANUALE';
                                    that.elencoTurni.push(turnoAlt);
                                }
                            }
                        }
                    }
                }
            }
        }

        // Resetto riferimenti di comodo per pubblicazione e calcolo, verranno valorizzati da un eventuale calcolo della giornata.
        that.tabFlex = null;
        that.tabQualificaEcc = null;
        that.tabQuantificaEcc = null;
        that.distribuzioneTurno = null;

        return {consolidatedData: that, calcolabile: calcolabile};
    };

    // Normalizza weeks su days dopo load o aggiornamento weeks
    static normalizzaDays = (that: Pick<GestionePlusData, 'days' | 'weeks'>) => {
        if (that.days && that.weeks)
            for (let indDay = 0; indDay < that.days.length; indDay++) {
                const day = that.days[indDay];
                for (let indWeek = 0; indWeek < that.weeks.length; ++indWeek) {
                    const week = that.weeks[indWeek];
                    if (StatoWeekPLUSRecord.contains(week, day)) {
                        day.week = week;
                    }
                }
            }
    }

    static getCausaleWF_MT = (data: GestionePlusData, day: PresenzeGiornataPlusRecord,) => {
        const causaliWF = (data.causaliWF && data.causaliWF.length > 0) ? data.causaliWF : [];
        const dtGiornata = DateTime.fromISO(day.dtGiornata!);
        let causaleWF: PresenzeCausaleWFRecord | undefined = causaliWF.find(item => {
            return item.tipoCausale === 'MANUTENZIONE_TIMBRATURE' &&
                !item.wf.annullata &&
                DateTime.fromISO(item.giornoDt!).equals(dtGiornata);
        });
        return causaleWF || null;
    }
}